// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  isDakotaModel,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
  runningStatusToTagType,
  runningStatusToText,
  weatherModeStatusToTagType,
  weatherModeStatusToText,
} from '../../../../../../plugins/crusher';
import {
  convertPressure,
  convertTemperature,
} from '../../../../../../plugins/general';
import {
  LUBE_FLOW_RANGE,
  LUBE_PRESSURE_RANGE,
  TEMPERATURE_RANGE,
} from '../../../../../../plugins/thresholds';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  PressureType,
  TemperatureType,
} from '../../../../../../types/global';
import { Theme } from '../../../../../../types/theme';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';

// View
import {
  CoolerFanMobileContent,
  CoolerFanStatusContainer,
  LubePressureStatusContainer,
  LubricationContainer,
  LubricationContent,
  MainContentRow,
  OverallStatusContainer,
  StatusContainer,
  ThermometersContainer,
  WinterModeStatusContainer,
} from './Lubrication.style';
import { LubricationProps } from './Lubrication.types';

function Lubrication({
  className,
  onOpenDrawer,
  unitData,
  edgeStatus,
  testId,
}: LubricationProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  const userPressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);
  const userTemperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  // Check if the model is Dakota (extract from operationalData)
  const isDakota: boolean = isDakotaModel(unitData?.crusherModelPLC);

  return (
    <LubricationContainer
      data-testid={testId}
      className={className}
    >
      <LubricationContent>
        <Row>
          <Col>
            <ModuleHeader
              onClick={onOpenDrawer}
              title={t('view.crusher.lubrication.title')}
              testId={`${testId}-module-header`}
            />
          </Col>
        </Row>
        {/* Status Row: Overall Status | Winter Mode | Cooler Fan */}
        <StatusContainer>
          <OverallStatusContainer>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
              preventOverflow
            >
              {t('view.crusher.overallStatus')}
            </Paragraph>
            <Tag
              text={overallStatusToText(unitData?.lubricationOverallStatus)}
              type={overallStatusToTagType(
                unitData?.lubricationOverallStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
            />
          </OverallStatusContainer>
          <WinterModeStatusContainer>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
              preventOverflow
            >
              {t('view.crusher.lubrication.winterMode')}
            </Paragraph>
            <Tag
              text={weatherModeStatusToText(unitData?.weatherModeStatus)}
              type={weatherModeStatusToTagType(
                unitData?.weatherModeStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
              className="status"
            />
          </WinterModeStatusContainer>
          <CoolerFanStatusContainer>
            <Paragraph
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
              preventOverflow
            >
              {t('view.crusher.lubrication.coolerFan')}
            </Paragraph>
            <Tag
              text={runningStatusToText(unitData?.coolerFanStatus)}
              type={runningStatusToTagType(
                unitData?.coolerFanStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
              className="status"
            />
          </CoolerFanStatusContainer>
        </StatusContainer>

        {/* Main Content: Thermometers + Cooler Fan (mobile) + Speedometer */}
        <MainContentRow>
          <Row style={{ flex: 1 }}>
            <Col
              xs={24}
              sm={!isDakota || (isDakota && unitData?.lubeFlowSwitchEnable) ? 16 : 24}
            >
              <ThermometersContainer>
                <Row style={{ width: '100%' }}>
                  <Col cols={unitData?.lubeSupplyTempEnable ? 8 : 12}>
                    <Thermometer
                      className="thermometer"
                      value={convertTemperature(
                        unitData?.lubricationReturnTemperature,
                        userTemperatureUnit,
                        1,
                      ).value}
                      minValue={
                        _.isNil(unitData?.lubricationReturnTemperature)
                          ? null
                          : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                      }
                      maxValue={
                        _.isNil(unitData?.lubricationReturnTemperature)
                          ? null
                          : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                      }
                      unit={userTemperatureUnit}
                      thresholds={{
                        lowAlarm: convertTemperature(
                          unitData?.spLubeReturnTempLowAlarm,
                          userTemperatureUnit,
                          1,
                        ).value,
                        lowWarning: convertTemperature(
                          unitData?.spLubeReturnTempLowWarning,
                          userTemperatureUnit,
                          1,
                        ).value,
                        highWarning: convertTemperature(
                          unitData?.spLubeReturnTempHighWarn,
                          userTemperatureUnit,
                          1,
                        ).value,
                        highAlarm: convertTemperature(
                          unitData?.spLubeReturnTempStopFeed,
                          userTemperatureUnit,
                          1,
                        ).value,
                      }}
                      title={`${t('view.crusher.lubrication.returnTemp')}`}
                      running={
                        isEquipmentRunning(
                          edgeStatus,
                          unitData?.timestamp,
                          DEVICE_COMMUNICATION_THRESHOLD,
                        )
                      }
                    />
                  </Col>
                  <Col cols={unitData?.lubeSupplyTempEnable ? 8 : 12}>
                    <Thermometer
                      className="thermometer"
                      maxValue={
                        _.isNil(unitData?.lubricationTankTemperature)
                          ? null
                          : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                      }
                      minValue={
                        _.isNil(unitData?.lubricationTankTemperature)
                          ? null
                          : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                      }
                      value={
                        convertTemperature(
                          unitData?.lubricationTankTemperature,
                          userTemperatureUnit,
                          1,
                        ).value
                      }
                      unit={userTemperatureUnit}
                      thresholds={{
                        lowAlarm: convertTemperature(
                          unitData?.spLubeTankTempLowAlarm,
                          userTemperatureUnit,
                          1,
                        ).value,
                        lowWarning: convertTemperature(
                          unitData?.spLubeTankTempLowWarning,
                          userTemperatureUnit,
                          1,
                        ).value,
                        highWarning: convertTemperature(
                          unitData?.spLubeTankTempHighWarning,
                          userTemperatureUnit,
                          1,
                        ).value,
                        highAlarm: convertTemperature(
                          unitData?.spLubeTankTempHighAlarm,
                          userTemperatureUnit,
                          1,
                        ).value,
                      }}
                      title={`${t('view.crusher.lubrication.tankTemp')}`}
                      running={
                        isEquipmentRunning(
                          edgeStatus,
                          unitData?.timestamp,
                          DEVICE_COMMUNICATION_THRESHOLD,
                        )
                      }
                    />
                  </Col>
                  {unitData?.lubeSupplyTempEnable && (
                  <Col cols={8}>
                    <Thermometer
                      className="thermometer"
                      minValue={
                        _.isNil(unitData?.lubricationSupplyTemperature)
                          ? null
                          : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                      }
                      maxValue={
                        _.isNil(unitData?.lubricationSupplyTemperature)
                          ? null
                          : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                      }
                      value={
                        convertTemperature(
                          unitData?.lubricationSupplyTemperature,
                          userTemperatureUnit,
                          1,
                        ).value
                      }
                      unit={userTemperatureUnit}
                      thresholds={{
                        lowWarning: convertTemperature(
                          unitData?.spLubeSupplyTempLowWarn,
                          userTemperatureUnit,
                          1,
                        ).value,
                      }}
                      title={`${t('view.crusher.lubrication.supplyTemp')}`}
                      running={
                        isEquipmentRunning(
                          edgeStatus,
                          unitData?.timestamp,
                          DEVICE_COMMUNICATION_THRESHOLD,
                        )
                      }
                    />
                  </Col>
                  )}
                </Row>
              </ThermometersContainer>
            </Col>

            {/* Cooler Fan - Mobile Only */}
            <Col xs={24} sm={0}>
              <CoolerFanMobileContent>
                <Paragraph
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                  color={theme.color.crusherDashboard.statusTitleColor}
                  preventOverflow
                >
                  {t('view.crusher.lubrication.coolerFan')}
                </Paragraph>
                <Tag
                  text={runningStatusToText(unitData?.coolerFanStatus)}
                  type={runningStatusToTagType(
                    unitData?.coolerFanStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )}
                  className="status"
                />
              </CoolerFanMobileContent>
            </Col>

            {(!isDakota || (isDakota && unitData?.lubeFlowSwitchEnable)) && (
              <Col xs={24} sm={8}>
                <LubePressureStatusContainer>
                  {!isDakota ? (
                    <Speedometer
                      className="speedometer"
                      minValue={
                        _.isNil(unitData?.lubricationOilPressure)
                          ? null
                          : LUBE_PRESSURE_RANGE(userPressureUnit).minValue
                      }
                      maxValue={
                        _.isNil(unitData?.lubricationOilPressure)
                          ? null
                          : LUBE_PRESSURE_RANGE(userPressureUnit).maxValue
                      }
                      value={
                        convertPressure(
                          unitData?.lubricationOilPressure,
                          userPressureUnit,
                          2,
                        ).value
                      }
                      decimalPlaces={
                        (() => {
                          // Show decimal places for values less than 1 (e.g., 0.15, 0.89, 0.00)
                          const convertedValue: number | null = convertPressure(
                            unitData?.lubricationOilPressure,
                            userPressureUnit,
                            2,
                          ).value;
                          return convertedValue !== null && convertedValue < 1 ? 2 : 0;
                        })()
                      }
                      unit={userPressureUnit}
                      thresholds={{
                        lowAlarm: convertPressure(
                          unitData?.spLubePressureLowAlarm,
                          userPressureUnit,
                          2,
                        ).value,
                        lowWarning: convertPressure(
                          unitData?.spLubePressureLowWarning,
                          userPressureUnit,
                          2,
                        ).value,
                      }}
                      title={`${t('view.crusher.lubrication.lubePressure')}`}
                      running={
                        isEquipmentRunning(
                          edgeStatus,
                          unitData?.timestamp,
                          DEVICE_COMMUNICATION_THRESHOLD,
                        )
                      }
                    />
                  ) : (
                    <Speedometer
                      className="speedometer"
                      minValue={
                        _.isNil(unitData?.lubeFlowScld)
                          ? null
                          : LUBE_FLOW_RANGE.minValue
                      }
                      maxValue={
                        _.isNil(unitData?.lubeFlowScld)
                          ? null
                          : LUBE_FLOW_RANGE.maxValue
                      }
                      value={unitData?.lubeFlowScld}
                      unit="GPM"
                      thresholds={{}}
                      title={`${t('view.crusher.lubrication.lubeFlow')}`}
                      running={
                        isEquipmentRunning(
                          edgeStatus,
                          unitData?.timestamp,
                          DEVICE_COMMUNICATION_THRESHOLD,
                        )
                      }
                    />
                  )}
                </LubePressureStatusContainer>
              </Col>
            )}
          </Row>
        </MainContentRow>

      </LubricationContent>
    </LubricationContainer>
  );
}

export { Lubrication };
